import React, { useEffect, useState, useRef } from 'react';
import { timesheetService } from './services/api';
import { TimesheetResponse, DailyTimesheet, Employee, Company, WeekInfo } from './types';
import './App.css';
import plumbuiltLogo from './images/plumbuilt-logo.png';
import plumbuiltLogoDark from './images/plumbuilt-logo-dark.png';
import Modal from './components/Modal';
import LoadingSpinner from './components/LoadingSpinner';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from './firebase';
import SignIn from './components/SignIn';

const useDebounce = (value: any, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const useApiCache = () => {
  const cache = useRef<{[key: string]: any}>({});
  
  const getCachedData = (key: string) => cache.current[key];
  const setCachedData = (key: string, data: any) => {
    cache.current[key] = data;
  };
  
  return { getCachedData, setCachedData };
};

// Update the grouping function
const groupEmployeesByPosition = (employees: Employee[]) => {
  const groups: Record<string, Employee[]> = {};
  
  // Define position mappings for standardization
  const positionMappings: Record<string, string> = {
    'Project Administrator': 'Administration',
    'Quantity Surveyor': 'Administration',
    'Operations Manager': 'Management',
    'Project Manager': 'Management',
    'Site Supervisor - Plumber': 'Supervision',
    'Foreman - Plumber': 'Supervision',
    'Technician - Plumber': 'Technicians',
    'Technician - Drainlayer': 'Technicians',
    'Level 1 - Apprentice': 'Apprentices',
    'Level 2 - Apprentice': 'Apprentices',
    'Level 3 - Apprentice': 'Apprentices',
    'Level 4 - Apprentice': 'Apprentices',
  };
  
  // Group employees by standardized position
  employees.forEach(emp => {
    let groupPosition = positionMappings[emp.Position] || emp.Position || 'Other';
    if (!groups[groupPosition]) {
      groups[groupPosition] = [];
    }
    groups[groupPosition].push(emp);
  });
  
  // Define group display order
  const groupOrder = [
    'Management',
    'Administration',
    'Supervision',
    'Technicians',
    'Apprentices',
    'Other'
  ];
  
  // Sort groups by defined order
  const sortedGroups: Record<string, Employee[]> = {};
  groupOrder.forEach(group => {
    if (groups[group]) {
      sortedGroups[group] = groups[group].sort((a, b) => 
        a.Name.localeCompare(b.Name)
      );
    }
  });
  
  // Add any remaining groups not in the order
  Object.keys(groups).forEach(group => {
    if (!sortedGroups[group]) {
      sortedGroups[group] = groups[group].sort((a, b) => 
        a.Name.localeCompare(b.Name)
      );
    }
  });
  
  return sortedGroups;
};

// Update loading state interface
interface LoadingState {
  initial: boolean;
  employees: boolean;
  timesheets: boolean;
  message: string;
}

function App() {
  // Auth state must be first
  const [user, setUser] = useState<any>(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoadingAuth(false);
    });
    return () => unsub();
  }, []);

  const [timesheetData, setTimesheetData] = useState<TimesheetResponse | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<number>(291);
  const [selectedCompany, setSelectedCompany] = useState<number>(2);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState<boolean>(false);
  const debouncedEmployee = useDebounce(selectedEmployee, 300);
  const debouncedCompany = useDebounce(selectedCompany, 300);
  const { getCachedData, setCachedData } = useApiCache();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);
  const [currentWeek, setCurrentWeek] = useState<WeekInfo>({
    startDate: '',
    endDate: '',
    offset: 0
  });
  const [isInitialEmployeeLoaded, setIsInitialEmployeeLoaded] = useState(false);

  // Unified loading state
  const [loadingState, setLoadingState] = useState<LoadingState>({
    initial: true,    // True on first load
    employees: false,
    timesheets: false,
    message: ''
  });

  const [isGeneratingReports, setIsGeneratingReports] = useState(false);

  // Add new state for report dialog
  const [showReportDialog, setShowReportDialog] = useState(false);

  // Add state for success modal
  const [successModal, setSuccessModal] = useState<{
    isOpen: boolean;
    message: string;
    reportPath: string;
    dateRange?: { start: string; end: string; }
  }>({
    isOpen: false,
    message: '',
    reportPath: ''
  });

  // Add email confirmation state
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [emailConfirmData, setEmailConfirmData] = useState<{
    recipientEmail: string;
    reportCount: number;
    generateAll: boolean;
    companyFilter?: number;
  }>({
    recipientEmail: '',
    reportCount: 0,
    generateAll: false
  });

  // Add theme state
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  // Add theme toggle handler
  const toggleTheme = () => {
    setIsDarkMode((prev: boolean) => {
      const newValue = !prev;
      localStorage.setItem('darkMode', JSON.stringify(newValue));
      return newValue;
    });
  };

  // Add this effect to handle html class
  useEffect(() => {
    document.documentElement.classList.toggle('dark-mode', isDarkMode);
    document.body.classList.toggle('dark-mode', isDarkMode);
  }, [isDarkMode]);

  // Add new state for weekly dashboard view
  const [isWeeklyDashboardView, setIsWeeklyDashboardView] = useState<boolean>(false);
  const [weeklySummaryData, setWeeklySummaryData] = useState<any>(null);
  const [isLoadingWeeklySummary, setIsLoadingWeeklySummary] = useState<boolean>(false);
  
  // Add new state for missing timesheets view
  const [isMissingTimesheetsView, setIsMissingTimesheetsView] = useState<boolean>(false);
  const [missingTimesheetsData, setMissingTimesheetsData] = useState<any>(null);
  const [isLoadingMissingTimesheets, setIsLoadingMissingTimesheets] = useState<boolean>(false);
  const [minHoursPerDay, setMinHoursPerDay] = useState<number>(8.0);

  const handleCompanyChange = async (companyId: number) => {
    console.log(`Switching to company ${companyId}`);
    setIsLoadingEmployees(true);
    setError(null);
    setSelectedCompany(companyId);
    setSelectedEmployee(0); // Reset employee selection
    setTimesheetData(null); // Clear timesheet data
    
    try {
      const response = await timesheetService.getEmployees(companyId);
      console.log('Employees fetched:', response.data);
      setEmployees(response.data);
      
      // Auto-select first employee if available
      if (response.data.length > 0) {
        setSelectedEmployee(response.data[0].ID);
      }
    } catch (err) {
      console.error('Error fetching employees:', err);
      setError('Failed to load employees');
      setEmployees([]);
    } finally {
      setIsLoadingEmployees(false);
    }
  };

  // Initial data load
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Start with loading state
        setLoadingState({
          initial: true,
          employees: true,
          timesheets: false,
          message: 'Loading initial data...'
        });

        // Get initial employee
        const initialResponse = await timesheetService.getInitialEmployee(selectedCompany);
        if (initialResponse.data) {
          setEmployees([initialResponse.data]);
          setSelectedEmployee(initialResponse.data.ID);

          // Load timesheet for initial employee
          const timesheetResponse = await timesheetService.getTimesheets(
            initialResponse.data.ID,
            selectedCompany,
            weekOffset
          );
          setTimesheetData(timesheetResponse);

          // Load rest of employees in background
          const fullEmployeeResponse = await timesheetService.getEmployees(selectedCompany);
          setEmployees(fullEmployeeResponse.data);
        }
      } catch (err) {
        console.error('Error in initial load:', err);
        setError('Failed to load initial data');
      } finally {
        // Clear loading state
        setLoadingState({
          initial: false,
          employees: false,
          timesheets: false,
          message: ''
        });
      }
    };

    loadInitialData();
  }, [selectedCompany]); // Only reload on company change

  // Handle employee selection
  const handleEmployeeChange = async (employeeId: number) => {
    setSelectedEmployee(employeeId);
    setLoadingState(prev => ({
      ...prev,
      timesheets: true,
      message: 'Loading timesheet data...'
    }));

    try {
      const response = await timesheetService.getTimesheets(
        employeeId,
        selectedCompany,
        weekOffset
      );
      setTimesheetData(response);
    } catch (err) {
      console.error('Error loading timesheet:', err);
      setError('Failed to load timesheet data');
    } finally {
      setLoadingState(prev => ({
        ...prev,
        timesheets: false,
        message: ''
      }));
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      console.error('Date formatting error:', e);
      return dateStr;
    }
  };

  // Use the grouping function
  const groupedEmployees = groupEmployeesByPosition(employees);

  // Update navigation functions
  const navigateWeek = async (direction: 'prev' | 'next') => {
    const newOffset = direction === 'next' ? weekOffset - 1 : weekOffset + 1;
    console.log(`Navigating to week offset: ${newOffset}`);
    setWeekOffset(newOffset);
    
    // Fetch new data based on current view
    if (isWeeklyDashboardView) {
      setIsLoadingWeeklySummary(true);
      try {
        const response = await timesheetService.getWeeklySummary(
          selectedCompany === 0 ? null : selectedCompany, 
          newOffset
        );
        setWeeklySummaryData(response);
      } catch (err) {
        setError('Error fetching weekly summary data');
        console.error(err);
      } finally {
        setIsLoadingWeeklySummary(false);
      }
    } else if (isMissingTimesheetsView) {
      setIsLoadingMissingTimesheets(true);
      try {
        const response = await timesheetService.getMissingTimesheets(
          selectedCompany === 0 ? null : selectedCompany, 
          newOffset,
          minHoursPerDay
        );
        setMissingTimesheetsData(response);
      } catch (err) {
        setError('Error fetching missing timesheets data');
        console.error(err);
      } finally {
        setIsLoadingMissingTimesheets(false);
      }
    } else if (selectedEmployee && selectedCompany) {
      setIsLoading(true);
      try {
        const response = await timesheetService.getTimesheets(
          selectedEmployee, 
          selectedCompany,
          newOffset
        );
        setTimesheetData(response);
      } catch (err) {
        setError('Error fetching timesheet data');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Also update the resetToCurrentWeek function
  const resetToCurrentWeek = async () => {
    console.log('Resetting to current week');
    setWeekOffset(0);
    
    // Fetch data for current week based on current view
    if (isWeeklyDashboardView) {
      setIsLoadingWeeklySummary(true);
      try {
        const response = await timesheetService.getWeeklySummary(
          selectedCompany === 0 ? null : selectedCompany, 
          0  // Reset to current week
        );
        setWeeklySummaryData(response);
      } catch (err) {
        setError('Error fetching weekly summary data');
        console.error(err);
      } finally {
        setIsLoadingWeeklySummary(false);
      }
    } else if (isMissingTimesheetsView) {
      setIsLoadingMissingTimesheets(true);
      try {
        const response = await timesheetService.getMissingTimesheets(
          selectedCompany === 0 ? null : selectedCompany, 
          0,  // Reset to current week
          minHoursPerDay
        );
        setMissingTimesheetsData(response);
      } catch (err) {
        setError('Error fetching missing timesheets data');
        console.error(err);
      } finally {
        setIsLoadingMissingTimesheets(false);
      }
    } else if (selectedEmployee && selectedCompany) {
      setIsLoading(true);
      try {
        const response = await timesheetService.getTimesheets(
          selectedEmployee, 
          selectedCompany,
          0  // Reset to current week
        );
        setTimesheetData(response);
      } catch (err) {
        setError('Error fetching timesheet data');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Update report generation handler
  const confirmGenerateReports = (generateAll: boolean, companyFilter?: number) => {
    // Get user email from Firebase
    const userEmail = user?.email || 'david.wilson@it360.co.nz';
    
    // Calculate number of reports
    let reportCount = 0;
    if (generateAll) {
      if (companyFilter) {
        // One company's employees
        reportCount = employees.filter(e => e.Company === (companyFilter === 2 ? 'Plumbuilt Plumbing' : 'Plumbuilt BOP')).length;
      } else {
        // All employees
        reportCount = employees.length;
      }
    } else {
      // Just one employee
      reportCount = 1;
    }
    
    // Set confirmation data
    setEmailConfirmData({
      recipientEmail: userEmail,
      reportCount,
      generateAll,
      companyFilter
    });
    
    // Show confirmation dialog
    setShowEmailConfirmation(true);
  };
  
  // Actual report generation handler - called after confirmation
  const handleGenerateReports = async (generateAll: boolean, companyFilter?: number, recipientEmail?: string) => {
    setIsGeneratingReports(true);
    try {
      const response = await timesheetService.generateReports(
        companyFilter || null,  // Pass null for both companies
        weekOffset,
        generateAll,
        generateAll ? undefined : selectedEmployee,
        recipientEmail
      );
      setShowReportDialog(false);
      setShowEmailConfirmation(false);
      setSuccessModal({
        isOpen: true,
        message: response.message,
        reportPath: response.reportPath,
        dateRange: response.date_range
      });
    } catch (err) {
      console.error('Error generating reports:', err);
      setError('Failed to generate reports');
    } finally {
      setIsGeneratingReports(false);
    }
  };

  const [showReportModal, setShowReportModal] = useState(false);
  const [reportResults, setReportResults] = useState<any>(null);

  const handleGenerateReport = async () => {
    try {
      const result = await timesheetService.generateReports(
        selectedCompany,
        weekOffset,
        false,
        selectedEmployee
      );
      
      setReportResults(result);
      setShowReportModal(true);
    } catch (error) {
      console.error('Error generating report:', error);
      // Handle error state
    }
  };

  // Add these handlers after your existing state declarations
  const handlePreviousEmployee = () => {
    const currentEmployee = employees.find(emp => emp.ID === selectedEmployee);
    if (!currentEmployee) return;
    
    // Find the current position group
    const currentPosition = Object.entries(groupedEmployees).find(([_, emps]) => 
      emps.some(emp => emp.ID === currentEmployee.ID)
    )?.[0];
    
    if (!currentPosition) return;
    
    // Get all employees in current position
    const currentGroup = groupedEmployees[currentPosition];
    const currentIndex = currentGroup.findIndex(emp => emp.ID === currentEmployee.ID);
    
    if (currentIndex > 0) {
      // Select previous employee in same group
      handleEmployeeChange(currentGroup[currentIndex - 1].ID);
    } else if (currentIndex === 0) {
      // Move to last employee of previous group
      const groupKeys = Object.keys(groupedEmployees);
      const currentGroupIndex = groupKeys.indexOf(currentPosition);
      if (currentGroupIndex > 0) {
        const previousGroup = groupedEmployees[groupKeys[currentGroupIndex - 1]];
        handleEmployeeChange(previousGroup[previousGroup.length - 1].ID);
      }
    }
  };

  const handleNextEmployee = () => {
    const currentEmployee = employees.find(emp => emp.ID === selectedEmployee);
    if (!currentEmployee) return;
    
    // Find the current position group
    const currentPosition = Object.entries(groupedEmployees).find(([_, emps]) => 
      emps.some(emp => emp.ID === currentEmployee.ID)
    )?.[0];
    
    if (!currentPosition) return;
    
    // Get all employees in current position
    const currentGroup = groupedEmployees[currentPosition];
    const currentIndex = currentGroup.findIndex(emp => emp.ID === currentEmployee.ID);
    
    if (currentIndex < currentGroup.length - 1) {
      // Select next employee in same group
      handleEmployeeChange(currentGroup[currentIndex + 1].ID);
    } else if (currentIndex === currentGroup.length - 1) {
      // Move to first employee of next group
      const groupKeys = Object.keys(groupedEmployees);
      const currentGroupIndex = groupKeys.indexOf(currentPosition);
      if (currentGroupIndex < groupKeys.length - 1) {
        const nextGroup = groupedEmployees[groupKeys[currentGroupIndex + 1]];
        handleEmployeeChange(nextGroup[0].ID);
      }
    }
  };

  // Add a function to handle loading weekly summary data
  const loadWeeklySummary = async () => {
    setIsLoadingWeeklySummary(true);
    setError(null);
    
    try {
      const response = await timesheetService.getWeeklySummary(
        selectedCompany === 0 ? null : selectedCompany, 
        weekOffset
      );
      setWeeklySummaryData(response);
      setIsWeeklyDashboardView(true);
    } catch (err) {
      console.error('Error loading weekly summary:', err);
      setError('Failed to load weekly summary data');
    } finally {
      setIsLoadingWeeklySummary(false);
    }
  };

  // Add a function to handle loading missing timesheets data
  const loadMissingTimesheets = async () => {
    setIsLoadingMissingTimesheets(true);
    setError(null);
    
    try {
      const response = await timesheetService.getMissingTimesheets(
        selectedCompany === 0 ? null : selectedCompany, 
        weekOffset,
        minHoursPerDay
      );
      setMissingTimesheetsData(response);
      setIsMissingTimesheetsView(true);
      
      // Disable other views
      setIsWeeklyDashboardView(false);
    } catch (err) {
      console.error('Error loading missing timesheets:', err);
      setError('Failed to load missing timesheets data');
    } finally {
      setIsLoadingMissingTimesheets(false);
    }
  };

  // Add a toggle function for switching between individual and weekly views
  const toggleWeeklyDashboard = async () => {
    if (!isWeeklyDashboardView) {
      // Loading weekly view
      await loadWeeklySummary();
    } else {
      // Switch back to individual view
      setIsWeeklyDashboardView(false);
      
      // Reload individual timesheet if we have a selected employee
      if (selectedEmployee) {
        setLoadingState(prev => ({
          ...prev,
          timesheets: true,
          message: 'Loading timesheet data...'
        }));
        
        try {
          const response = await timesheetService.getTimesheets(
            selectedEmployee,
            selectedCompany,
            weekOffset
          );
          setTimesheetData(response);
        } catch (err) {
          console.error('Error loading timesheet:', err);
          setError('Failed to load timesheet data');
        } finally {
          setLoadingState(prev => ({
            ...prev,
            timesheets: false,
            message: ''
          }));
        }
      }
    }
  };

  // Add a toggle function for missing timesheets view
  const toggleMissingTimesheets = async () => {
    if (!isMissingTimesheetsView) {
      // Loading missing timesheets view
      await loadMissingTimesheets();
    } else {
      // Switch back to individual view
      setIsMissingTimesheetsView(false);
      
      // Reload individual timesheet if we have a selected employee
      if (selectedEmployee) {
        setLoadingState(prev => ({
          ...prev,
          timesheets: true,
          message: 'Loading timesheet data...'
        }));
        
        try {
          const response = await timesheetService.getTimesheets(
            selectedEmployee,
            selectedCompany,
            weekOffset
          );
          setTimesheetData(response);
        } catch (err) {
          console.error('Error loading timesheet:', err);
          setError('Failed to load timesheet data');
        } finally {
          setLoadingState(prev => ({
            ...prev,
            timesheets: false,
            message: ''
          }));
        }
      }
    }
  };

  // Add function to get productivity class based on rate
  const getProductivityClass = (rate: number) => {
    if (rate >= 80) return 'productivity-high';
    if (rate >= 60) return 'productivity-medium';
    return 'productivity-low';
  };

  // Add Weekly Dashboard component
  const WeeklyDashboardView = () => {
    if (!weeklySummaryData) return <div>No data available</div>;
    
    const { companies, total, date_range } = weeklySummaryData;
    
    return (
      <div className="weekly-summary">
        <h2>Weekly Summary Dashboard</h2>
        
        {/* Overall Summary Card */}
        <div className="summary-grid">
          <div className="summary-card">
            <h3>Time Overview</h3>
            <div className="metric">
              <span className="label">Period:</span>
              <span className="value">
                {formatDate(date_range.start)} - {formatDate(date_range.end)}
              </span>
            </div>
            <div className="metric">
              <span className="label">Total Employees:</span>
              <span className="value">{total.employee_count}</span>
            </div>
            <div className="metric">
              <span className="label">Average Hours/Employee:</span>
              <span className="value">{total.average_hours_per_employee.toFixed(1)}</span>
            </div>
          </div>

          <div className="summary-card">
            <h3>Productivity</h3>
            <div className="metric">
              <span className="label">Billable Hours:</span>
              <span className="value">{total.billable_hours.toFixed(1)}</span>
            </div>
            <div className="metric">
              <span className="label">Non-Billable Hours:</span>
              <span className="value">{total.non_billable_hours.toFixed(1)}</span>
            </div>
            <div className="metric highlight">
              <span className="label">Productivity Rate:</span>
              <span className={`value ${getProductivityClass(total.productivity_rate)}`}>
                {total.productivity_rate.toFixed(1)}%
              </span>
            </div>
          </div>

          <div className="summary-card">
            <h3>Financial</h3>
            <div className="metric">
              <span className="label">Total Cost:</span>
              <span className="value">${total.total_cost.toFixed(2)}</span>
            </div>
            <div className="metric">
              <span className="label">Average Cost/Hour:</span>
              <span className="value">${total.average_cost_per_hour.toFixed(2)}</span>
            </div>
          </div>
        </div>
        
        {/* Company Breakdown */}
        {Object.entries(companies).map(([companyId, companyData]: [string, any]) => (
          <div key={companyId} className="company-summary">
            <div className="company-header">
              <h3>{companyData.name}</h3>
              <div>
                <span className={`productivity-badge ${getProductivityClass(companyData.productivity_rate)}`}>
                  {companyData.productivity_rate.toFixed(1)}% Productivity
                </span>
              </div>
            </div>
            
            <div className="company-metrics">
              <table className="summary-table">
                <thead>
                  <tr>
                    <th>Position</th>
                    <th>Employees</th>
                    <th>Total Hours</th>
                    <th>Billable Hours</th>
                    <th>Productivity</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(companyData.positions).map(([position, posData]: [string, any]) => (
                    <tr key={position}>
                      <td>{position}</td>
                      <td>{posData.employee_count}</td>
                      <td>{posData.total_hours.toFixed(1)}</td>
                      <td>{posData.billable_hours.toFixed(1)}</td>
                      <td className={getProductivityClass(posData.billable_hours / posData.total_hours * 100)}>
                        {(posData.billable_hours / posData.total_hours * 100).toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <h4>Top Performing Employees</h4>
            <table className="summary-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Position</th>
                  <th>Total Hours</th>
                  <th>Productivity</th>
                  <th>Cost</th>
                </tr>
              </thead>
              <tbody>
                {companyData.employees
                  .sort((a: any, b: any) => b.productivity_rate - a.productivity_rate)
                  .slice(0, 5)
                  .map((employee: any) => (
                    <tr key={employee.id}>
                      <td>{employee.name}</td>
                      <td>{employee.position}</td>
                      <td>{employee.total_hours.toFixed(1)}</td>
                      <td className={getProductivityClass(employee.productivity_rate)}>
                        {employee.productivity_rate.toFixed(1)}%
                      </td>
                      <td>${employee.total_cost.toFixed(2)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    );
  };

  // Add Missing Timesheets component
  const MissingTimesheetsView = () => {
    if (!missingTimesheetsData) return <div>No data available</div>;
    
    const { companies, date_range, min_hours_per_day } = missingTimesheetsData;
    
    // Format a date for display
    const formatDateFull = (dateStr: string) => {
      if (!dateStr) return '';
      try {
        return new Date(dateStr).toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric'
        });
      } catch (e) {
        console.error('Date formatting error:', e);
        return dateStr;
      }
    };
    
    return (
      <div className="missing-timesheets">
        <h2>Missing Timesheets Report</h2>
        
        <div className="report-info">
          <div>
            <p>
              <strong>Period:</strong> {formatDate(date_range.start)} - {formatDate(date_range.end)}
            </p>
            <p>
              <strong>Expected hours per day:</strong> {min_hours_per_day}
            </p>
            <p className="report-note">
              <em>Note: Only past and current dates are checked. Today is {formatDateFull(date_range.today)}.</em>
            </p>
          </div>
          <div className="hours-input">
            <label htmlFor="min-hours">Minimum hours per day: </label>
            <input 
              type="number" 
              id="min-hours" 
              min="1" 
              max="24" 
              step="0.5" 
              value={minHoursPerDay} 
              onChange={(e) => {
                setMinHoursPerDay(parseFloat(e.target.value));
                // Debounce the reload
                const timer = setTimeout(() => loadMissingTimesheets(), 500);
                return () => clearTimeout(timer);
              }}
            />
          </div>
        </div>
        
        {/* Company Sections */}
        {Object.entries(companies).map(([companyId, companyData]: [string, any]) => (
          <div key={companyId} className="company-summary">
            <div className="company-header">
              <h3>{companyData.name}</h3>
              <div className="company-stats">
                <span>{companyData.employees_with_missing_timesheets} employees with issues</span>
                <span>{companyData.incomplete_days_total} incomplete days total</span>
              </div>
            </div>
            
            {companyData.employees.length > 0 ? (
              <table className="summary-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Position</th>
                    <th>Missing Days</th>
                    <th>Incomplete Days</th>
                    <th>Total Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {companyData.employees.map((employee: any) => (
                    <tr key={employee.id}>
                      <td>{employee.name}</td>
                      <td>{employee.position}</td>
                      <td>
                        {employee.missing_days.length > 0 ? (
                          <div className="days-list">
                            {employee.missing_days.map((day: string) => (
                              <div key={day} className="missing-day">
                                {formatDateFull(day)}
                              </div>
                            ))}
                          </div>
                        ) : (
                          "None"
                        )}
                      </td>
                      <td>
                        {employee.incomplete_days.length > 0 ? (
                          <div className="days-list">
                            {employee.incomplete_days.map((day: any) => (
                              <div key={day.date} className="incomplete-day">
                                {formatDateFull(day.date)}: {day.hours_logged.toFixed(1)} hrs 
                                (missing {day.hours_missing.toFixed(1)} hrs)
                              </div>
                            ))}
                          </div>
                        ) : (
                          "None"
                        )}
                      </td>
                      <td>{employee.missing_days.length + employee.incomplete_days.length}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="no-issues">
                <p>All employees have complete timesheets for this period! 🎉</p>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  if (loadingAuth) {
    return <div style={{textAlign: 'center', marginTop: '3em'}}>Loading...</div>;
  }

  if (!user) {
    return <SignIn onAuth={() => {}} />;
  }

  if (error) return <div className="error">{error}</div>;
  
  return (
    <>
      <div className={`page-wrapper ${isDarkMode ? 'dark-mode' : ''}`} />
      <div className={`App ${isDarkMode ? 'dark-mode' : ''}`}>
        <header className="header">
          <img 
            src={isDarkMode ? plumbuiltLogoDark : plumbuiltLogo} 
            alt="Plumbuilt Logo" 
            className="logo" 
          />
          <h1>SimPro Dashboard</h1>
          <div className="header-buttons">
            <button 
              className="theme-toggle"
              onClick={toggleTheme}
              aria-label="Toggle theme"
              title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDarkMode ? '☀️' : '🌙'}
            </button>
            <button 
              className={`weekly-dashboard-button ${isWeeklyDashboardView ? 'active' : ''} ${isLoadingWeeklySummary ? 'loading' : ''}`}
              onClick={toggleWeeklyDashboard}
              disabled={isLoadingWeeklySummary || isLoadingMissingTimesheets}
            >
              {isLoadingWeeklySummary ? 'Loading...' : isWeeklyDashboardView ? 'Individual View' : 'Weekly Dashboard'}
            </button>
            <button 
              className={`missing-timesheets-button ${isMissingTimesheetsView ? 'active' : ''} ${isLoadingMissingTimesheets ? 'loading' : ''}`}
              onClick={toggleMissingTimesheets}
              disabled={isLoadingMissingTimesheets || isLoadingWeeklySummary}
            >
              {isLoadingMissingTimesheets ? 'Loading...' : isMissingTimesheetsView ? 'Individual View' : 'Missing Timesheets'}
            </button>
            <button 
              className={`generate-reports-button ${isGeneratingReports ? 'loading' : ''}`}
              onClick={() => setShowReportDialog(true)}
              disabled={isGeneratingReports}
            >
              {isGeneratingReports ? 'Generating...' : 'Generate Reports'}
            </button>
            <button 
              onClick={() => signOut(auth)}
              className="signout-button"
            >
              Sign Out
            </button>
          </div>
        </header>
        
        {/* Company Selection - always visible */}
        <div className="company-select">
          <label>Select Company: </label>
          <div className="toggle-buttons">
            <button 
              className={`toggle-button ${selectedCompany === 2 ? 'active' : ''}`}
              onClick={() => handleCompanyChange(2)}
              disabled={isLoadingEmployees}
            >
              Plumbuilt Plumbing
            </button>
            <button 
              className={`toggle-button ${selectedCompany === 3 ? 'active' : ''}`}
              onClick={() => handleCompanyChange(3)}
              disabled={isLoadingEmployees}
            >
              Plumbuilt BOP
            </button>
          </div>
        </div>

        {/* Loading indicator - only show on initial load */}
        {loadingState.initial && (
          <div className="loading-indicator initial">
            <div className="loading-spinner large">
              <svg className="spinner-svg" viewBox="0 0 50 50" width="32" height="32">
                <circle
                  className="spinner-circle"
                  cx="25"
                  cy="25"
                  r="20"
                  fill="none"
                  strokeWidth="4"
                />
              </svg>
              <span className="loading-text">Loading initial data...</span>
            </div>
          </div>
        )}

        {/* Employee Selection - show when not in initial load and not in weekly view */}
        {!loadingState.initial && !isWeeklyDashboardView && (
          <div className="employee-select">
            <label htmlFor="employee">Select Employee: </label>
            <div className="employee-select-container">
              <button
                onClick={handlePreviousEmployee}
                className="toggle-button employee-nav"
                disabled={loadingState.employees}
              >
                ←
              </button>
              <select 
                id="employee"
                value={selectedEmployee}
                onChange={(e) => handleEmployeeChange(Number(e.target.value))}
                disabled={loadingState.employees}
                className="employee-dropdown"
              >
                <option value={0}>Select an employee...</option>
                {Object.entries(groupedEmployees).map(([position, emps]) => (
                  <optgroup key={position} label={position}>
                    {emps.map(emp => (
                      <option key={emp.ID} value={emp.ID}>
                        {emp.Name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <button
                onClick={handleNextEmployee}
                className="toggle-button employee-nav"
                disabled={loadingState.employees}
              >
                →
              </button>
            </div>
          </div>
        )}

        {/* Week Navigation - Always visible if data is loaded */}
        {(timesheetData || weeklySummaryData) && (
          <div className="week-navigation">
            <button 
              onClick={() => navigateWeek('prev')}
              className="nav-button"
            >
              ← Previous Week
            </button>
            <button
              onClick={resetToCurrentWeek}
              className={`nav-button current-week-button ${weekOffset === 0 ? 'active' : ''}`}
              disabled={weekOffset === 0}
            >
              This Week
            </button>
            <span className="current-week">
              {isWeeklyDashboardView 
                ? `${formatDate(weeklySummaryData.date_range.start)} - ${formatDate(weeklySummaryData.date_range.end)}`
                : timesheetData && `${formatDate(timesheetData.summary.date_range.start)} - ${formatDate(timesheetData.summary.date_range.end)}`
              }
            </span>
            <button 
              onClick={() => navigateWeek('next')}
              className="nav-button"
              disabled={weekOffset <= -1}
            >
              Next Week →
            </button>
          </div>
        )}

        {/* Loading indicators */}
        {(isLoadingEmployees || isLoading || isLoadingWeeklySummary) && !loadingState.initial && (
          <div className="loading-indicator subtle">
            {isLoadingEmployees && "Loading employees..."}
            {isLoading && "Loading timesheet data..."}
            {isLoadingWeeklySummary && "Loading weekly summary..."}
          </div>
        )}

        {/* Enhanced loading indicator for weekly and missing timesheets views */}
        {isWeeklyDashboardView && isLoadingWeeklySummary && (
          <div className="loading-indicator enhanced">
            <div className="loading-spinner large">
              <svg className="spinner-svg" viewBox="0 0 50 50" width="48" height="48">
                <circle
                  className="spinner-circle"
                  cx="25"
                  cy="25"
                  r="20"
                  fill="none"
                  strokeWidth="4"
                />
              </svg>
              <span className="loading-text">Loading weekly summary...</span>
            </div>
          </div>
        )}
        {isMissingTimesheetsView && isLoadingMissingTimesheets && (
          <div className="loading-indicator enhanced">
            <div className="loading-spinner large">
              <svg className="spinner-svg" viewBox="0 0 50 50" width="48" height="48">
                <circle
                  className="spinner-circle"
                  cx="25"
                  cy="25"
                  r="20"
                  fill="none"
                  strokeWidth="4"
                />
              </svg>
              <span className="loading-text">Loading missing timesheets...</span>
            </div>
          </div>
        )}

        {/* Conditional rendering based on view mode */}
        {!loadingState.initial && (
          <>
            {isWeeklyDashboardView ? (
              // Weekly Dashboard View
              <WeeklyDashboardView />
            ) : isMissingTimesheetsView ? (
              // Missing Timesheets View
              <MissingTimesheetsView />
            ) : (
              // Individual Employee View
              loadingState.timesheets ? (
                <div className="loading-indicator subtle">
                  Loading timesheet data...
                </div>
              ) : (
                timesheetData && (
                  <>
                    {timesheetData.data && (
                      <>
                        {/* Summary Section */}
                        <div className="summary-grid">
                          <div className="summary-card">
                            <h3>Time Overview</h3>
                            <div className="metric">
                              <span className="label">Period:</span>
                              <span className="value">
                                {formatDate(timesheetData.summary.date_range.start)} - {formatDate(timesheetData.summary.date_range.end)}
                              </span>
                            </div>
                            <div className="metric">
                              <span className="label">Total Days Worked:</span>
                              <span className="value">{timesheetData.summary.total_days}</span>
                            </div>
                            <div className="metric">
                              <span className="label">Average Hours/Day:</span>
                              <span className="value">{timesheetData.summary.averages.hours_per_day.toFixed(1)}</span>
                            </div>
                          </div>

                          <div className="summary-card">
                            <h3>Productivity</h3>
                            <div className="metric">
                              <span className="label">Billable Hours:</span>
                              <span className="value">{timesheetData.summary.billable_hours.toFixed(1)}</span>
                            </div>
                            <div className="metric">
                              <span className="label">Non-Billable Hours:</span>
                              <span className="value">{timesheetData.summary.non_billable_hours.toFixed(1)}</span>
                            </div>
                            <div className="metric highlight">
                              <span className="label">Productivity Rate:</span>
                              <span className="value">{timesheetData.summary.productivity_rate.toFixed(1)}%</span>
                            </div>
                          </div>

                          <div className="summary-card">
                            <h3>Financial</h3>
                            <div className="metric">
                              <span className="label">Total Cost:</span>
                              <span className="value">${timesheetData.summary.total_cost.toFixed(2)}</span>
                            </div>
                            <div className="metric">
                              <span className="label">Average Cost/Hour:</span>
                              <span className="value">${timesheetData.summary.averages.cost_per_hour.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>

                        {/* Daily Timesheets */}
                        <h2>Timesheet Entries</h2>
                        {Object.entries(timesheetData.data)
                          .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
                          .map(([date, dayData]) => (
                            <div key={date} className="day-section">
                              <h3>{new Date(date).toLocaleDateString('en-US', { 
                                weekday: 'long', 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric' 
                              })}</h3>
                              <table>
                                <thead>
                                  <tr>
                                    <th>Time</th>
                                    <th>Type</th>
                                    <th>Job Details</th>
                                    <th>Hours</th>
                                    <th>Cost</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {dayData.entries.map((entry, index) => (
                                    <tr key={`${entry._href}-${index}`}>
                                      <td>{entry.StartTime} - {entry.EndTime}</td>
                                      <td>{entry.ScheduleType}</td>
                                      <td className="job-details">
                                        {entry.Reference}
                                      </td>
                                      <td>{entry.TotalHrs.toFixed(2)}</td>
                                      <td>${entry.Cost.toFixed(2)}</td>
                                    </tr>
                                  ))}
                                  {/* Add totals row */}
                                  <tr className="totals-row">
                                    <td colSpan={3} className="totals-label">Daily Totals:</td>
                                    <td>{dayData.total_hours.toFixed(2)}</td>
                                    <td>${dayData.total_cost.toFixed(2)}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          ))}
                      </>
                    )}
                  </>
                )
              )
            )}
          </>
        )}

        {/* Add loading indicators */}
        {isLoadingEmployees && (
          <div className="loading-indicator">
            Loading employees... Please wait.
          </div>
        )}
        
        {isLoading && (
          <div className="loading-indicator">
            Loading timesheet data... Please wait.
          </div>
        )}
        
        {error && (
          <div className="error-message">
            Error: {error}
          </div>
        )}

        {/* Report Generation Dialog */}
        <Modal 
          isOpen={showReportDialog}
          onClose={() => !isGeneratingReports && setShowReportDialog(false)}
          title={isGeneratingReports ? "Generating Reports..." : "Generate Reports"}
        >
          <div className="dialog-content">
            {isGeneratingReports ? (
              <div className="generating-state">
                <LoadingSpinner size="large" />
                <p>Please wait while your reports are being generated...</p>
              </div>
            ) : (
              <>
                <div className="dialog-header">
                  <h3>Generate timesheet reports for:</h3>
                </div>
                <div className="dialog-body">
                  <div className="dialog-buttons">
                    <button 
                      className="primary-button"
                      onClick={() => confirmGenerateReports(false)}
                      disabled={isGeneratingReports}
                    >
                      Current Employee ({employees.find(e => e.ID === selectedEmployee)?.Name})
                    </button>
                    <button 
                      className="secondary-button"
                      onClick={() => confirmGenerateReports(true, selectedCompany)}
                      disabled={isGeneratingReports}
                    >
                      {selectedCompany === 2 ? 'Plumbuilt Plumbing' : 'Plumbuilt BOP'} Staff
                    </button>
                    <button 
                      className="secondary-button"
                      onClick={() => confirmGenerateReports(true)}
                      disabled={isGeneratingReports}
                    >
                      All Staff (Both Companies)
                    </button>
                    <button 
                      onClick={() => setShowReportDialog(false)}
                      className="cancel-button"
                      disabled={isGeneratingReports}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </Modal>

        {/* Success Modal */}
        <Modal 
          isOpen={successModal.isOpen} 
          onClose={() => setSuccessModal(prev => ({ ...prev, isOpen: false }))}
          title="Success!"
        >
          <div className="dialog-content">
            <div className="dialog-body">
              <p>{successModal.message}</p>
              <div className="report-details">
                <p><strong>Location:</strong> {successModal.reportPath}</p>
                {successModal.dateRange && (
                  <p><strong>Period:</strong> {formatDate(successModal.dateRange.start)} to {formatDate(successModal.dateRange.end)}</p>
                )}
              </div>
            </div>
            <div className="dialog-buttons">
              <button 
                className="primary-button"
                onClick={() => setSuccessModal(prev => ({ ...prev, isOpen: false }))}
              >
                OK
              </button>
            </div>
          </div>
        </Modal>

        {/* Email Confirmation Dialog */}
        <Modal
          isOpen={showEmailConfirmation}
          onClose={() => !isGeneratingReports && setShowEmailConfirmation(false)}
          title="Confirm Email Reports"
        >
          <div className="dialog-content">
            {isGeneratingReports ? (
              <div className="generating-state">
                <LoadingSpinner size="large" />
                <p>Please wait while your reports are being sent...</p>
              </div>
            ) : (
              <>
                <div className="dialog-header">
                  <h3>Email Confirmation</h3>
                </div>
                <div className="dialog-body">
                  <p>
                    Are you sure you want to send {emailConfirmData.reportCount === 1 ? 'one report ' : `${emailConfirmData.reportCount} reports `}
                    to the following email address?
                  </p>
                  <div className="email-input-container">
                    <input 
                      type="email"
                      value={emailConfirmData.recipientEmail}
                      onChange={(e) => setEmailConfirmData(prev => ({ ...prev, recipientEmail: e.target.value }))}
                      className="email-input"
                      placeholder="Email address"
                    />
                  </div>
                  <div className="dialog-buttons">
                    <button 
                      className="primary-button"
                      onClick={() => handleGenerateReports(
                        emailConfirmData.generateAll,
                        emailConfirmData.companyFilter,
                        emailConfirmData.recipientEmail
                      )}
                      disabled={!emailConfirmData.recipientEmail || isGeneratingReports}
                    >
                      Confirm & Send Reports
                    </button>
                    <button 
                      onClick={() => setShowEmailConfirmation(false)}
                      className="cancel-button"
                      disabled={isGeneratingReports}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </Modal>

        <Modal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          title="Report Generation Results"
        >
          {reportResults && (
            <div>
              <p>{reportResults.message}</p>
              {reportResults.results.map((result: any, index: number) => (
                <div 
                  key={index} 
                  className={`report-result ${result.success ? 'success' : 'failure'}`}
                >
                  <p>Employee: {result.employee}</p>
                  <p>Status: {result.success ? 'Success' : 'Failed'}</p>
                </div>
              ))}
              {reportResults.reportPath && (
                <p>Reports generated in: {reportResults.reportPath}</p>
              )}
            </div>
          )}
        </Modal>
      </div>
    </>
  );
}

export default App;
